import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  NOTIFICATION_TYPES,
  isCrmNotificationType,
  isEquipmentNotificationType,
  isChatNotificationType,
  type NotificationLevel,
  type NotificationTier,
} from "@/lib/notification-catalog";
import { type NotificationType } from "@/hooks/use-notifications";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

const TIER_META: Record<NotificationTier, { title: string; description: string }> = {
  personal: {
    title: "Personlig",
    description: "Varsler om dine egne oppgaver og timebank.",
  },
  project: {
    title: "Prosjekt",
    description: "Varsler knyttet til prosjekter du er involvert i.",
  },
  leadership: {
    title: "Ledelse",
    description: "Varsler for ledelse og godkjenninger.",
  },
};

const LEVEL_OPTIONS: { value: NotificationLevel; label: string }[] = [
  { value: "off", label: "Av" },
  { value: "in_app", label: "Kun i appen" },
  { value: "email", label: "App + e-post" },
];

interface NotificationTierSectionProps {
  tier: NotificationTier;
  /** Aktivt nivå per type (allerede fallback til default i parent). */
  levelByType: Record<NotificationType, NotificationLevel>;
  onChange: (type: NotificationType, level: NotificationLevel) => void;
  disabled?: boolean;
}

export function NotificationTierSection({
  tier,
  levelByType,
  onChange,
  disabled,
}: NotificationTierSectionProps) {
  const meta = TIER_META[tier];
  const crmEnabled = useFeatureFlag("feature_crm_ko_pipe");
  const equipmentEnabled = useFeatureFlag("feature_equipment");
  const chatEnabled = useFeatureFlag("feature_chat_rom");
  const types = (Object.keys(NOTIFICATION_TYPES) as NotificationType[]).filter(
    (t) =>
      NOTIFICATION_TYPES[t].tier === tier &&
      (crmEnabled || !isCrmNotificationType(t)) &&
      (equipmentEnabled || !isEquipmentNotificationType(t)) &&
      (chatEnabled || !isChatNotificationType(t)),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{meta.title}</CardTitle>
        <CardDescription>{meta.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {types.map((type) => (
          <div
            key={type}
            className="flex flex-col gap-2 border-b border-border pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="text-sm font-medium">{NOTIFICATION_TYPES[type].label}</span>
            <RadioGroup
              value={levelByType[type]}
              onValueChange={(value) => onChange(type, value as NotificationLevel)}
              disabled={disabled}
              className="flex flex-wrap gap-4"
            >
              {LEVEL_OPTIONS.map((opt) => {
                const id = `${type}-${opt.value}`;
                return (
                  <div key={opt.value} className="flex items-center gap-2">
                    <RadioGroupItem value={opt.value} id={id} />
                    <Label htmlFor={id} className="text-sm font-normal">
                      {opt.label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
